import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_URL } from '../../config/api';

const EMPTY_FIELDS = {
  stylistName: null,
  customerName: null,
  barcode: null,
  orderType: null,
  servicePrice: null,
  warrantyInfo: null,
  pickupDate: null,
  qcNotes: null,
  rooms: [],
};

export default function DraftOrderModal({ isOpen, onClose, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [bubbles, setBubbles] = useState([]);
  const [collectedFields, setCollectedFields] = useState(EMPTY_FIELDS);
  const [isComplete, setIsComplete] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const [micError, setMicError] = useState(null);
  const [editedRoomsText, setEditedRoomsText] = useState('');

  const recognitionRef = useRef(null);
  const sessionIdRef = useRef(0);
  const chatEndRef = useRef(null);

  // Keep a ref to the latest send function to avoid stale closures in speech callbacks
  const sendUserMessageRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [bubbles, liveTranscript]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setBubbles([]);
      setCollectedFields(EMPTY_FIELDS);
      setIsComplete(false);
      setSaveStatus(null);
      setTextInput('');
      setLiveTranscript('');
      setBubbles([{
        from: 'ai',
        text: "Hi! Tell me about the order — press 🎙 to start speaking, press again when you're done. Or type below.",
      }]);
    }
  }, [isOpen]);

  const sendUserMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading || isComplete) return;

    const userMsg = { role: 'user', content: text };

    // Use functional updater to get the latest messages without stale closure
    let latestMessages;
    setMessages(prev => {
      latestMessages = [...prev, userMsg];
      return latestMessages;
    });

    setBubbles(prev => [...prev, { from: 'user', text }]);
    setIsLoading(true);

    // collectedFields captured from closure is fine here — this fn is recreated when it changes
    const currentFields = collectedFields;
    const currentMessages = messages;
    const updatedMessages = [...currentMessages, userMsg];

    try {
      const res = await fetch(`${API_URL}/api/draft-orders/interview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages, collectedFields: currentFields }),
      });

      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();

      const merged = mergeFields(currentFields, data.updatedFields || {});
      setCollectedFields(merged);
      setIsComplete(data.isComplete === true);

      const aiText = data.nextQuestion || 'Got it!';
      setMessages(prev => [...prev, { role: 'assistant', content: aiText }]);

      if (data.completionMessages && data.completionMessages.length > 0) {
        const [details, rooms] = data.completionMessages;
        setBubbles(prev => [
          ...prev,
          { from: 'ai', text: details },
          ...(rooms ? [{ from: 'ai', text: rooms, type: 'editable-rooms' }] : []),
        ]);
        if (rooms) setEditedRoomsText(rooms);
      } else {
        setBubbles(prev => [...prev, { from: 'ai', text: aiText }]);
      }
    } catch {
      setBubbles(prev => [...prev, { from: 'ai', text: 'Something went wrong. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, collectedFields, isLoading]);

  // Keep the ref in sync so the speech recognition callback always calls the latest version
  useEffect(() => {
    sendUserMessageRef.current = sendUserMessage;
  }, [sendUserMessage]);

  const handleMicClick = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (isListening) {
      recognitionRef.current?.stop();
      // onend will fire, send the transcript, and call setIsListening(false)
      return;
    }

    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in this browser. Please use Chrome.');
      return;
    }

    // Each press gets a unique session ID — stale handlers and retry timers are no-ops
    const sessionId = ++sessionIdRef.current;

    function startAttempt(attemptsLeft) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let finalTranscript = '';
      let gotError = false;

      recognition.onresult = (e) => {
        if (sessionIdRef.current !== sessionId) return;
        let interim = '';
        finalTranscript = '';
        for (let i = 0; i < e.results.length; i++) {
          if (e.results[i].isFinal) {
            finalTranscript += e.results[i][0].transcript + ' ';
          } else {
            interim += e.results[i][0].transcript;
          }
        }
        setLiveTranscript((finalTranscript + interim).trim());
      };

      recognition.onend = () => {
        if (sessionIdRef.current !== sessionId) return;
        // onerror already handled — skip the duplicate onend Chrome fires after an error
        if (gotError) return;
        const text = finalTranscript.trim();
        finalTranscript = '';
        setLiveTranscript('');
        setIsListening(false);
        if (text && sendUserMessageRef.current) {
          sendUserMessageRef.current(text);
        }
      };

      recognition.onerror = (e) => {
        if (sessionIdRef.current !== sessionId) return;
        gotError = true;
        finalTranscript = '';
        setLiveTranscript('');
        if (e.error === 'network' && attemptsLeft > 0) {
          setMicError('Connecting… retrying');
          setTimeout(() => {
            if (sessionIdRef.current !== sessionId) return;
            startAttempt(attemptsLeft - 1);
          }, 1500);
        } else if (e.error === 'network') {
          setIsListening(false);
          setMicError('Mic unavailable — please type below.');
        } else if (e.error !== 'no-speech') {
          setIsListening(false);
          console.error('Speech error:', e.error);
          setMicError(`Mic error (${e.error}) — tap 🎙 to try again.`);
        }
      };

      recognitionRef.current = recognition;

      try {
        recognition.start();
      } catch (err) {
        console.error('Failed to start mic:', err);
        setIsListening(false);
      }
    }

    setIsListening(true);
    setLiveTranscript('');
    setMicError(null);
    startAttempt(3);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    sendUserMessage(textInput.trim());
    setTextInput('');
  };

  const handleSaveDraft = async () => {
    setSaveStatus('saving');
    try {
      const rawTranscript = messages
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join(' | ');

      // If the stylist edited the rooms textarea, parse it back into structured rooms
      const finalFields = editedRoomsText
        ? { ...collectedFields, rooms: parseRoomsFromText(editedRoomsText) }
        : collectedFields;

      const res = await fetch(`${API_URL}/api/draft-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          createdBy: currentUser || 'unknown',
          rawTranscript,
          parsedJson: finalFields,
        }),
      });

      if (!res.ok) throw new Error('Save failed');
      setSaveStatus('saved');
      setTimeout(onClose, 1500);
    } catch {
      setSaveStatus('error');
    }
  };

  if (!isOpen) return null;

  const hasMeaningfulData = collectedFields.stylistName || collectedFields.customerName || collectedFields.rooms?.length > 0;

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.headerTitle}>AI Order Assistant</span>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {/* Collected fields summary strip */}
        {hasMeaningfulData && (
          <div style={styles.summaryStrip}>
            {collectedFields.stylistName && (
              <span style={{ ...styles.badge, background: '#fce7f3', color: '#9d174d' }}>✂️ {collectedFields.stylistName}</span>
            )}
            {collectedFields.customerName && (
              <span style={styles.badge}>👤 {collectedFields.customerName}</span>
            )}
            {collectedFields.orderType && (
              <span style={styles.badge}>📋 {collectedFields.orderType}</span>
            )}
            {collectedFields.barcode && (
              <span style={styles.badge}>🏷 {collectedFields.barcode}</span>
            )}
            {collectedFields.warrantyInfo?.status === 'active' && (
              <span style={{ ...styles.badge, background: '#dbeafe', color: '#1e40af' }}>🛡 Warranty expires {collectedFields.warrantyInfo.expiresDate}</span>
            )}
            {collectedFields.warrantyInfo?.status === 'expired' && (
              <span style={{ ...styles.badge, background: '#fee2e2', color: '#991b1b' }}>⚠️ Out of warranty</span>
            )}
            {collectedFields.servicePrice != null && (
              <span style={{ ...styles.badge, background: '#dcfce7', color: '#166534' }}>💰 {collectedFields.servicePrice || 'No charge'}</span>
            )}
            {collectedFields.pickupDate && (
              <span style={styles.badge}>📅 {collectedFields.pickupDate}</span>
            )}
            {collectedFields.qcNotes != null && (
              <span style={{ ...styles.badge, background: '#fef9c3', color: '#713f12' }}>
                🔍 QC: {collectedFields.qcNotes || '(none)'}
              </span>
            )}
            {collectedFields.rooms?.map((r, i) => (
              <span key={i} style={styles.badgeRoom}>🏠 {r.roomName}</span>
            ))}
          </div>
        )}

        {/* Chat area */}
        <div style={styles.chatArea}>
          {bubbles.map((b, i) => {
            if (b.type === 'editable-rooms') {
              return (
                <div key={i} style={styles.editableRoomsBubble}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>
                    ✏️ Tap to correct any room notes before saving
                  </div>
                  <textarea
                    style={styles.roomsTextarea}
                    value={editedRoomsText}
                    onChange={e => setEditedRoomsText(e.target.value)}
                    rows={Math.max(4, editedRoomsText.split('\n').length + 1)}
                  />
                </div>
              );
            }
            return (
              <div key={i} style={b.from === 'user' ? styles.userBubble : styles.aiBubble}>
                {b.text.split('\n').map((line, j) => (
                  <span key={j} style={{ display: 'block' }}>{line}</span>
                ))}
              </div>
            );
          })}
          {isLoading && (
            <div style={styles.aiBubble}>
              <span style={styles.dots}>• • •</span>
            </div>
          )}
          {/* Live transcript preview while mic is active */}
          {isListening && (
            <div style={styles.livePreview}>
              <span style={styles.listeningDot} /> {liveTranscript || 'Listening…'}
            </div>
          )}
          {/* Mic error notice */}
          {micError && !isListening && (
            <div style={styles.micErrorBubble}>{micError}</div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input row */}
        <form onSubmit={handleTextSubmit} style={styles.inputRow}>
          <button
            type="button"
            onClick={handleMicClick}
            style={{ ...styles.micBtn, ...(isListening ? styles.micBtnActive : {}) }}
            title={isListening ? 'Tap to finish speaking' : 'Tap to start speaking'}
          >
            {isListening ? '⏹' : '🎙'}
          </button>
          <input
            style={styles.textInput}
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            placeholder={isListening ? 'Mic is active — press ⏹ when done' : 'Or type here…'}
            disabled={isListening || isLoading}
          />
          <button type="submit" style={styles.sendBtn} disabled={!textInput.trim() || isLoading || isListening}>
            Send
          </button>
        </form>

        {/* Footer actions */}
        <div style={styles.footer}>
          {isComplete && saveStatus !== 'saved' && (
            <button onClick={handleSaveDraft} disabled={saveStatus === 'saving'} style={styles.saveBtn}>
              {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'error' ? 'Retry Save' : 'Save Draft Order'}
            </button>
          )}
          {!isComplete && hasMeaningfulData && (
            <button
              onClick={handleSaveDraft}
              disabled={saveStatus === 'saving'}
              style={{ ...styles.saveBtn, background: '#6c757d' }}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save Partial Draft'}
            </button>
          )}
          {saveStatus === 'saved' && <span style={{ color: '#28a745', fontWeight: 600 }}>Draft saved!</span>}
          {saveStatus === 'error' && <span style={{ color: '#dc3545' }}>Save failed — try again</span>}
        </div>
      </div>
    </div>
  );
}

function parseRoomsFromText(text) {
  return text
    .split('\n')
    .filter(l => l.trim().startsWith('🏠'))
    .map(line => {
      const content = line.replace(/^🏠\s*/, '').trim();
      const colon = content.indexOf(':');
      if (colon === -1) return { roomName: content, taskNotes: '' };
      return { roomName: content.slice(0, colon).trim(), taskNotes: content.slice(colon + 1).trim() };
    })
    .filter(r => r.roomName);
}

function mergeFields(existing, incoming) {
  const merged = { ...existing };
  if (incoming.stylistName) merged.stylistName = incoming.stylistName;
  if (incoming.customerName) merged.customerName = incoming.customerName;
  if (incoming.barcode) merged.barcode = incoming.barcode;
  if (incoming.orderType) merged.orderType = incoming.orderType;
  if (incoming.servicePrice != null) merged.servicePrice = incoming.servicePrice;
  if (incoming.warrantyInfo != null) merged.warrantyInfo = incoming.warrantyInfo;
  if (incoming.pickupDate) merged.pickupDate = incoming.pickupDate;
  if (incoming.qcNotes != null) merged.qcNotes = incoming.qcNotes;
  if (Array.isArray(incoming.rooms) && incoming.rooms.length) {
    const existingNames = new Set((existing.rooms || []).map(r => r.roomName?.toLowerCase()));
    const newRooms = incoming.rooms.filter(r => r.roomName && !existingNames.has(r.roomName.toLowerCase()));
    merged.rooms = [...(existing.rooms || []), ...newRooms];
  }
  return merged;
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#fff', borderRadius: 12,
    width: '100%', maxWidth: 520, maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', background: '#1a1a2e', color: '#fff',
  },
  headerTitle: { fontWeight: 700, fontSize: 16 },
  closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' },
  summaryStrip: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    padding: '8px 14px', background: '#f0f4ff', borderBottom: '1px solid #dce4ff',
  },
  badge: {
    background: '#e0e7ff', color: '#3730a3',
    borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600,
  },
  badgeRoom: {
    background: '#dcfce7', color: '#166534',
    borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600,
  },
  chatArea: {
    flex: 1, overflowY: 'auto',
    padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 10,
    minHeight: 200, maxHeight: 380,
  },
  userBubble: {
    alignSelf: 'flex-end',
    background: '#1a1a2e', color: '#fff',
    borderRadius: '16px 16px 4px 16px',
    padding: '10px 14px', maxWidth: '78%', fontSize: 14, lineHeight: 1.5,
  },
  aiBubble: {
    alignSelf: 'flex-start',
    background: '#f3f4f6', color: '#111',
    borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px', maxWidth: '78%', fontSize: 14, lineHeight: 1.5,
  },
  micErrorBubble: {
    alignSelf: 'flex-start',
    background: '#fee2e2', color: '#991b1b',
    borderRadius: '16px 16px 16px 4px',
    padding: '8px 14px', fontSize: 13, maxWidth: '78%',
  },
  livePreview: {
    alignSelf: 'flex-start',
    background: '#fef9c3', color: '#555',
    borderRadius: '16px 16px 16px 4px',
    padding: '10px 14px', maxWidth: '78%', fontSize: 14,
    fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 8,
  },
  listeningDot: {
    width: 10, height: 10, borderRadius: '50%',
    background: '#dc2626', flexShrink: 0,
    animation: 'pulse 1s infinite',
  },
  dots: { letterSpacing: 4, fontSize: 18, color: '#888' },
  inputRow: {
    display: 'flex', gap: 8, padding: '10px 14px',
    borderTop: '1px solid #1260fc', alignItems: 'center',
  },
  micBtn: {
    width: 42, height: 42, borderRadius: '50%',
    border: '2px solid #d1d5db', background: '#201e1e',
    fontSize: 20, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  micBtnActive: { background: '#faa3a3', border: '2px solid #dc2626' },
  textInput: {
    flex: 1, border: '1px solid #d1d5db', borderRadius: 8,
    padding: '8px 12px', fontSize: 14, outline: 'none',
  },
  sendBtn: {
    background: '#1a1a2e', color: '#fff',
    border: 'none', borderRadius: 8,
    padding: '8px 16px', fontSize: 14, cursor: 'pointer', flexShrink: 0,
  },
  footer: {
    padding: '10px 14px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10,
  },
  saveBtn: {
    background: '#16a34a', color: '#fff',
    border: 'none', borderRadius: 8,
    padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  editableRoomsBubble: {
    alignSelf: 'flex-start',
    background: 'var(--dark-bg)', color: 'var(--text-primary)',
    border: '1px solid #86efac',
    borderRadius: '16px 16px 16px 4px',
    padding: '8px 12px',
  },
  roomsTextarea: {
    width: '100%', border: '1px solid #86efac', borderRadius: 8,
    padding: '8px 10px', fontSize: 13, lineHeight: 1.6,
    fontFamily: 'inherit', resize: 'vertical',
    background: 'var(--dark-bg)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
  },
};
